import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import { AgGridModule } from 'ag-grid-angular';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { ProvisionsService } from '../../service/entity-services/provisions.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';

import { EntityList } from '../../objects/entity-list';
import { Provision } from '../../objects/provision';

import * as provisionColumns from '../../shared/entity-columns/provisions-columns';
import { NewWorkflowRequestButtonComponent } from '../../shared/components/new-workflow-request-button/new-workflow-request-button.component';

@Component({
  selector: 'app-provisions-list',
  templateUrl: './provisions-list.component.html',
  styleUrls: ['./provisions-list.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    NewWorkflowRequestButtonComponent
  ]
})
export class ProvisionsListComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private cdr = inject(ChangeDetectorRef);
  public provisionsService = inject(ProvisionsService);

  @ViewChild('provisionsSidenavActions') provisionsSidenavActions!: TemplateRef<any>;

  // Observable for provisions
  allProvisions$: Observable<Provision[]> = this.provisionsService.allProvisions$;

  // UI state
  columnChooserControl = new FormControl();
  customizeColumnsUi = false;
  waitingForServer = false;
  showNameSavedView = false;
  isInitializing = true;

  // Permissions - use signal from permissions store
  isViewer = this.permissionsStore.isViewer;

  // Column configuration
  defaultCols: any[] = [
    provisionColumns.colName,
    provisionColumns.colDescription,
    provisionColumns.colOwner,
    provisionColumns.colImportantDate,
    provisionColumns.colRequiredAction,
    provisionColumns.colRiskDomain,
    provisionColumns.colRiskName,
    provisionColumns.colActionCompleted
  ];

  selectedCols = this.defaultCols;

  // Column customization properties
  availableColumns: any[] = [];
  availableColumnSelectList: { value: any; viewValue: string }[] = [];
  columnSelectorControl = new FormControl<string>('');
  selectedColumnsList: any[] = [];
  filteredColumns$!: Observable<{ value: any; viewValue: string }[]>;

  // AG-Grid default column configuration
  public defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab']
  };
  public showFloatingFilters = false;

  // Enable tooltips for cells with truncated content
  public tooltipShowDelay = 300;      // Show after 300ms hover
  public tooltipHideDelay = 10000;    // Keep visible for 10 seconds after mouse leaves
  public tooltipInteraction = true;   // Allow hovering over tooltip to keep it visible

  // AG Grid configuration
  public frameworkComponents = {};

  // Quick filter state for Approved / Contract Provisions
  activeQuickFilter: 'all' | 'approved' | 'contract' = 'all';
  quickFilterLabel = '';

  constructor() {
    super();
    this.entityType = 'provisions';
    this.buildAvailableColumns();
    this.initializeSessionStorage();
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Provisions');

    // Set up autocomplete filtering for column selector
    this.filteredColumns$ = this.columnSelectorControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterColumns(value || ''))
    );
  }

  /**
   * Override onGridReady to complete initialization after grid is ready
   */
  override onGridReady(params: any): void {
    super.onGridReady(params);

    this.completeInitialization().catch(err => {
      console.error('[onGridReady] Error during initialization:', err);
      this.isInitializing = false;
    });
  }

  /**
   * Completes the grid initialization after grid is ready.
   */
  private async completeInitialization(): Promise<void> {
    this.suspendSessionTracking = true;

    const hasSessionState = this.sessionStorageKey && sessionStorage.getItem(this.sessionStorageKey);

    if (hasSessionState) {
      this.loadSessionState();
      this.suspendSessionTracking = false;
    } else {
      await this.loadUserViews();

      const hasNewFormatView = this.savedNamedViews && (this.savedNamedViews as any).gridState;
      const hasOldFormatView =
        this.savedNamedViews && ((this.savedNamedViews as any).columnState || (this.savedNamedViews as any).savedViewData);

      if (hasNewFormatView || hasOldFormatView) {
        await this.loadNamedView(this.savedNamedViews);
        this.suspendSessionTracking = false;
      } else {
        this.autoSizeAll();
        await new Promise(resolve => setTimeout(resolve, 1100));
      }
    }

    this.isInitializing = false;
    this.cdr.detectChanges();
  }

  /**
   * Builds the list of available columns
   */
  private buildAvailableColumns(): void {
    this.availableColumns = [...provisionColumns.allColumns];

    // Filter out non-column objects
    this.availableColumns = this.availableColumns.filter(col => col && col.colId && col.headerName);

    // Remove duplicates by colId
    const uniqueColsMap = new Map();
    this.availableColumns.forEach(col => {
      if (!uniqueColsMap.has(col.colId)) {
        uniqueColsMap.set(col.colId, col);
      }
    });
    this.availableColumns = Array.from(uniqueColsMap.values());

    // Build and sort select list
    this.availableColumnSelectList = this.availableColumns.map(col => ({
      value: col,
      viewValue: col.headerName
    }));
    this.availableColumnSelectList.sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  /**
   * Filters available columns based on user input
   */
  private _filterColumns(value: string | any): { value: any; viewValue: string }[] {
    if (!value || typeof value !== 'string') {
      return this.availableColumnSelectList;
    }
    const filterValue = value.toLowerCase();
    return this.availableColumnSelectList.filter(option => option.viewValue.toLowerCase().includes(filterValue));
  }

  /**
   * Display function for autocomplete - returns empty string to keep input clear
   */
  displayColumnFn(): string {
    return '';
  }

  ngAfterViewInit(): void {
    this.sidenavContentService.updateSidenavContent(this.provisionsSidenavActions);
  }

  ngOnDestroy(): void {
    this.sidenavContentService.updateSidenavContent(null);
  }

  // Navigation
  createNewProvision(): void {
    this.router.navigateByUrl('/securehome/new-provision');
  }

  // Grid events
  onCellClicked(event: any): void {
    console.log('Provision clicked:', event.data);
    if (event.data?.provNum) {
      this.router.navigate(['/securehome/provision', event.data.provNum]);
    }
  }

  onSearchChange(searchText: string): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchText);
    }
  }

  // Column management methods
  selectColumn(column: any): void {
    if (column && !this.selectedColumnsList.find(c => c.colId === column.colId)) {
      this.selectedColumnsList.push(column);
      setTimeout(() => {
        this.columnSelectorControl.setValue('', { emitEvent: false });
      }, 0);
    }
  }

  removeColumn(column: any): void {
    const index = this.selectedColumnsList.findIndex(c => c.colId === column.colId);
    if (index >= 0) {
      this.selectedColumnsList.splice(index, 1);
    }
  }

  applyColumns(): void {
    if (this.gridApi && this.selectedColumnsList.length > 0) {
      this.selectedCols = this.selectedColumnsList;
      this.gridApi.setGridOption('columnDefs', this.selectedCols);
      this.customizeColumnsUi = false;
      this.markSessionAsModified();
    }
  }

  openCustomizeColumnsDialog(): void {
    if (this.gridApi) {
      const currentState = this.gridApi.getColumnState();
      const visibleColIds = currentState.filter((col: any) => !col.hide).map((col: any) => col.colId);

      this.selectedColumnsList = this.availableColumns.filter(col => visibleColIds.includes(col.colId));
    } else {
      this.selectedColumnsList = [...this.selectedCols];
    }

    this.customizeColumnsUi = true;
  }

  /**
   * Applies a quick filter to show only approved provisions
   */
  filterApprovedProvisions(): void {
    if (!this.gridApi) return;

    // Ensure the approvedProvision column exists in the grid (even if hidden)
    this.ensureColumnExists('approvedProvision');

    const filterInstance = this.gridApi.getFilterInstance('approvedProvision');
    if (filterInstance) {
      filterInstance.setModel({
        filterType: 'text',
        type: 'equals',
        filter: 'Yes'
      });
      this.gridApi.onFilterChanged();
    }

    this.activeQuickFilter = 'approved';
    this.quickFilterLabel = 'Showing Approved Provisions Only';
  }

  /**
   * Applies a quick filter to show only AI-generated contract provisions
   */
  filterContractProvisions(): void {
    if (!this.gridApi) return;

    // Ensure the aiGenerated column exists in the grid (even if hidden)
    this.ensureColumnExists('aiGenerated');

    const filterInstance = this.gridApi.getFilterInstance('aiGenerated');
    if (filterInstance) {
      filterInstance.setModel({
        filterType: 'text',
        type: 'equals',
        filter: 'Yes'
      });
      this.gridApi.onFilterChanged();
    }

    this.activeQuickFilter = 'contract';
    this.quickFilterLabel = 'Showing Contract Provisions Only (AI-Generated)';
  }

  /**
   * Clears the quick filter and shows all provisions
   */
  clearQuickFilter(): void {
    if (!this.gridApi) return;

    this.gridApi.setFilterModel(null);
    this.gridApi.onFilterChanged();
    this.activeQuickFilter = 'all';
    this.quickFilterLabel = '';
    this.updateFiltersAppliedStatus();
  }

  /**
   * Ensures a column exists in the current grid column defs.
   * If missing, adds it as hidden so its filter can be used.
   */
  private ensureColumnExists(colId: string): void {
    const existingCol = this.gridApi.getColumn(colId);
    if (!existingCol) {
      // Find the column definition from availableColumns
      const colDef = this.availableColumns.find(c => c.colId === colId);
      if (colDef) {
        const currentDefs = this.gridApi.getGridOption('columnDefs') || [];
        this.gridApi.setGridOption('columnDefs', [...currentDefs, { ...colDef, hide: true }]);
      }
    }
  }

  toggleFloatingFilters(): void {
    this.showFloatingFilters = !this.showFloatingFilters;

    if (this.gridApi) {
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      allColumnIds.forEach(colId => {
        this.gridApi.setColumnFilterProperty(colId, 'floatingFilter', this.showFloatingFilters);
      });

      this.gridApi.refreshHeader();
    }
  }

  // Export functions
  exportCSV(): void {
    if (this.gridApi) {
      const params = {
        processCellCallback: this.formattingFunction,
        fileName: 'provisions_export.csv'
      };
      this.gridApi.exportDataAsCsv(params);
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      const excelParams = {
        processCellCallback: this.formattingFunction,
        fileName: 'provisions_export.xlsx'
      };
      this.gridApi.exportDataAsExcel(excelParams);
    }
  }
}
